import React, { useState, forwardRef, useImperativeHandle } from "react";
import { useSelector } from "react-redux";
import DatePickerComponent from "./Datepicker";
import "./DateRangePicker.css";

const DateRangePicker = forwardRef(({ onDateRangeChange }, ref) => {
  const culture = useSelector(state => state.i18n.culture);

  // Use separate state for start and end timestamps
  const [startTimestamp, setStartTimestamp] = useState(null);
  const [endTimestamp, setEndTimestamp] = useState(null);
  const [error, setError] = useState("");

  useImperativeHandle(ref, () => ({
    clearDateRange: () => {
      setStartTimestamp(null);
      setEndTimestamp(null);
      onDateRangeChange(null, null);
    },
  }));

  // Handle start date changes
  const handleStartDateChange = timestamp => {
    setStartTimestamp(timestamp);
    validateDateRange(timestamp, endTimestamp);
  };

  // Handle end date changes
  const handleEndDateChange = timestamp => {
    setEndTimestamp(timestamp);
    validateDateRange(startTimestamp, timestamp);
  };

  // Validate that start date is before end date
  const validateDateRange = (start, end) => {
    setError("");

    if (start && end) {
      // Convert timestamps to date objects for comparison
      const startDate = timestampToDate(start);
      const endDate = timestampToDate(end);

      if (startDate > endDate) {
        setError("Start date must be before end date");
        // Only pass valid ranges to parent
        onDateRangeChange(start, null);
      } else {
        onDateRangeChange(start, end);
      }
    } else {
      // If either date is null, just pass the values as is
      onDateRangeChange(start, end);
    }
  };

  // Helper function to convert timestamp to Date
  const timestampToDate = timestamp => {
    if (!timestamp) return null;

    const year = parseInt(timestamp.substring(0, 4));
    const month = parseInt(timestamp.substring(4, 6)) - 1;
    const day = parseInt(timestamp.substring(6, 8));
    return new Date(year, month, day);
  };

  return (
    <div className="date-range-picker">
      <div className="date-range-container">
        <DatePickerComponent
          timestamp={startTimestamp}
          handleChange={handleStartDateChange}
          placeholderText={"Start Date"}
          locale={culture}
        />
        <span className="date-range-separator">-</span>
        <DatePickerComponent
          timestamp={endTimestamp}
          handleChange={handleEndDateChange}
          placeholderText={"End Date"}
          locale={culture}
        />
      </div>
      {error && <div className="error-message">{error}</div>}
    </div>
  );
});

export default DateRangePicker;
